import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { Radio, Loader2 } from 'lucide-react';

/**
 * Interface for RFID analytics data point
 */
export interface RFIDAnalyticsData {
  date: string;
  accuracy: number; // Percentage (0-100)
  signalStrength: number; // Percentage (0-100)
}

interface RFIDAnalyticsChartProps {
  data: RFIDAnalyticsData[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

/**
 * Custom tooltip component for RFID analytics
 */
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const accuracy = payload.find(p => p.dataKey === 'accuracy')?.value || 0;
    const signalStrength = payload.find(p => p.dataKey === 'signalStrength')?.value || 0;

    const getStatusColor = (value: number) => {
      if (value >= 90) return 'text-green-600';
      if (value >= 70) return 'text-yellow-600';
      return 'text-red-600';
    };

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-700 mb-2">{label}</p>
        <div className="space-y-1">
          <p className={`${getStatusColor(accuracy as number)}`}>
            Accuracy: <span className="font-bold">{accuracy}%</span>
          </p>
          <p className={`${getStatusColor(signalStrength as number)}`}>
            Signal: <span className="font-bold">{signalStrength}%</span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

/**
 * RFIDAnalyticsChart - Line chart showing RFID accuracy and signal strength trend
 *
 * Features:
 * - Displays daily RFID accuracy percentage
 * - Tracks signal strength over time
 * - Purple/indigo color scheme for technical data
 * - Dual-line visualization for comparison
 * - Interactive tooltips with performance indicators
 * - Responsive design for all devices
 * - Loading and error states
 *
 * @param data - Array of daily RFID analytics data
 * @param loading - Loading state indicator
 * @param error - Error message if data fetch failed
 * @param height - Chart height in pixels (default: 350)
 */
const RFIDAnalyticsChart: React.FC<RFIDAnalyticsChartProps> = ({
  data,
  loading = false,
  error = null,
  height = 350,
}) => {
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        <span className="ml-2 text-gray-600">Loading RFID analytics...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <p className="text-red-600 font-semibold">Failed to load chart</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center text-gray-500">
          <Radio className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No RFID analytics data available</p>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const avgAccuracy = (data.reduce((sum, item) => sum + item.accuracy, 0) / data.length).toFixed(1);
  const avgSignalStrength = (data.reduce((sum, item) => sum + item.signalStrength, 0) / data.length).toFixed(1);
  const maxAccuracy = Math.max(...data.map(item => item.accuracy));
  const minAccuracy = Math.min(...data.map(item => item.accuracy));

  // Get performance status
  const getPerformanceStatus = (value: number) => {
    if (value >= 90) return { label: 'Excellent', color: 'bg-green-100 text-green-700' };
    if (value >= 70) return { label: 'Good', color: 'bg-yellow-100 text-yellow-700' };
    return { label: 'Needs Attention', color: 'bg-red-100 text-red-700' };
  };

  const accuracyStatus = getPerformanceStatus(parseFloat(avgAccuracy));

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <Radio className="w-5 h-5 mr-2 text-purple-600" />
          RFID System Analytics
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Tracking accuracy and signal strength performance
        </p>
      </div>

      {/* Statistics Summary */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-purple-50 p-3 rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-gray-600">Average Accuracy</p>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${accuracyStatus.color}`}>
              {accuracyStatus.label}
            </span>
          </div>
          <p className="text-2xl font-bold text-purple-600">{avgAccuracy}%</p>
          <p className="text-xs text-gray-500 mt-1">
            Range: {minAccuracy}% - {maxAccuracy}%
          </p>
        </div>
        <div className="bg-indigo-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600 mb-1">Average Signal</p>
          <p className="text-2xl font-bold text-indigo-600">{avgSignalStrength}%</p>
          <div className="w-full h-2 bg-gray-200 rounded-full mt-2">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"
              style={{ width: `${avgSignalStrength}%` }}
            ></div>
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="date"
            stroke="#666"
            tick={{ fontSize: 12 }}
            tickMargin={10}
          />
          <YAxis
            stroke="#666"
            tick={{ fontSize: 12 }}
            domain={[0, 100]}
            label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="line"
          />
          <Line
            type="monotone"
            dataKey="accuracy"
            name="RFID Accuracy (%)"
            stroke="#9333ea"
            strokeWidth={3}
            dot={{ fill: '#9333ea', r: 4 }}
            activeDot={{ r: 6, fill: '#7e22ce' }}
            animationDuration={1000}
          />
          <Line
            type="monotone"
            dataKey="signalStrength"
            name="Signal Strength (%)"
            stroke="#6366f1"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#6366f1', r: 3 }}
            activeDot={{ r: 5, fill: '#4f46e5' }}
            animationDuration={1000}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Performance Indicators */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-3 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-700">System Status</span>
            <div className="flex items-center">
              <div className={`w-2 h-2 rounded-full mr-2 ${
                parseFloat(avgAccuracy) >= 90 ? 'bg-green-500 animate-pulse' :
                parseFloat(avgAccuracy) >= 70 ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'
              }`}></div>
              <span className="text-xs font-semibold text-gray-700">
                {parseFloat(avgAccuracy) >= 90 ? 'Optimal' :
                 parseFloat(avgAccuracy) >= 70 ? 'Normal' : 'Alert'}
              </span>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-3 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-700">Reliability Score</span>
            <span className="text-xs font-bold text-indigo-600">
              {((parseFloat(avgAccuracy) + parseFloat(avgSignalStrength)) / 2).toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      {/* Alert Section */}
      {parseFloat(avgAccuracy) < 70 && (
        <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-3 rounded">
          <p className="text-sm text-red-800 font-semibold">
            ⚠️ RFID accuracy below optimal threshold
          </p>
          <p className="text-xs text-red-700 mt-1">
            Consider checking reader positioning and tag conditions
          </p>
        </div>
      )}
    </div>
  );
};

export default RFIDAnalyticsChart;
